import { TaskRecordPicker } from './TaskRecordPicker';
import { TaskScheduleFields } from './TaskScheduleFields';
import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Option,
  Button,
  Stack,
  Grid,
  Alert,
  CircularProgress,
} from '@mui/joy';
// Date picker imports removed for simplicity
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../lib/api';
import { getOrganizationTimezone, parseFromDatetimeLocalInput } from '../../lib/timezoneUtils';
import type { Task, TaskCreateData, TaskTemplate, TaskType, TaskPriority } from '../../types/tasks';
import type { Job, Opportunity } from '../../types';
import { useTaskMutations } from '../../hooks/useTasks';
import { useOrganizationUsers } from '../../hooks/useOrganizationUsers';
import { useAuth } from '../../contexts/AuthContext';
import { applyPrefill } from '../../utils/prefill';

interface TaskCreateModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (task: Task) => void;
  prefilledJobId?: number;
  prefilledOpportunityId?: number;
  /** Day the task should default to, as `YYYY-MM-DD` (e.g. the calendar cell that was clicked). */
  defaultDueDate?: string;
  timezone?: string;
}

const taskTypeOptions: { value: TaskType; label: string }[] = [
  { value: 'phone_call', label: 'Phone Call' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'site_visit', label: 'Site Visit' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'installation', label: 'Installation' },
  { value: 'repair', label: 'Repair' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'documentation', label: 'Documentation' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'material_order', label: 'Material Order' },
  { value: 'scheduling', label: 'Scheduling' },
  { value: 'cleanup', label: 'Cleanup' },
  { value: 'other', label: 'Other' },
];

export const TaskCreateModal: React.FC<TaskCreateModalProps> = ({
  open,
  onClose,
  onSuccess,
  prefilledJobId,
  prefilledOpportunityId,
  defaultDueDate,
  timezone = getOrganizationTimezone(),
}) => {
  const { user: currentUser } = useAuth();
  const [formData, setFormData] = useState<Partial<TaskCreateData>>({
    name: '',
    description: '',
    task_type: 'other',
    priority: 'medium',
    status: 'not_started',
    is_all_day: false,
    completion_percentage: 0,
    related_job: prefilledJobId,
    related_opportunity: prefilledOpportunityId,
    assigned_to: undefined,
  });
  const [selectedTemplate, setSelectedTemplate] = useState<TaskTemplate | null>(null);

  // Fetch organization users
  const { users, loading: usersLoading } = useOrganizationUsers();

  const jobContext = useQuery({
    queryKey: ['task-form-job', formData.related_job], enabled: open && !!formData.related_job,
    queryFn: async () => (await apiClient.get<Job>(`/api/jobs/${formData.related_job}/`)).data,
  });
  const opportunityContext = useQuery({
    queryKey: ['task-form-opportunity', formData.related_opportunity], enabled: open && !!formData.related_opportunity,
    queryFn: async () => (await apiClient.get<Opportunity>(`/api/crm/opportunities/${formData.related_opportunity}/`)).data,
  });

  // Use shared task mutations hook
  const { createTask } = useTaskMutations();

  // Seed what the system already knows each time the modal opens: whoever is
  // creating the task is its default owner, and a task launched from a
  // calendar cell is due on the day that was clicked. Only empty fields are
  // touched, so anything already typed survives.
  const seededDueDateRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!open) return;

    const seededDueDate = defaultDueDate ? parseFromDatetimeLocalInput(`${defaultDueDate}T09:00`, timezone).toISOString() : undefined;
    // Read the previous guess before the updater runs: React may defer the
    // updater to the next render, by which point the ref would already hold
    // the new value and the comparison below would never match.
    const previousGuess = seededDueDateRef.current;
    seededDueDateRef.current = seededDueDate;

    setFormData(prev => {
      // This modal stays mounted between openings, so a date it seeded itself
      // would otherwise stick when the user goes back and clicks a different
      // day. Replacing our own last guess is not clobbering their input.
      const stale =
        seededDueDate && prev.due_date && prev.due_date === previousGuess
          ? { ...prev, due_date: undefined }
          : prev;

      return applyPrefill(stale, { assigned_to: currentUser?.id, due_date: seededDueDate });
    });
  }, [open, currentUser?.id, defaultDueDate, timezone]);

  /**
   * Contacts to carry over from the related record.
   *
   * The form has no contact picker, so `related_contacts` is inherited-only —
   * it always reflects the selected job/opportunity and can never clobber
   * something the user typed.
   */
  const inheritedContactIds = (): number[] => {
    if (formData.related_job) {
      const job = jobContext.data;
      const ids = [job?.associated_contact?.id, ...(job?.contacts || []).map((c) => c.id)];
      return Array.from(new Set(ids.filter((id): id is number => !!id)));
    }
    if (formData.related_opportunity) {
      const opportunity = opportunityContext.data;
      return opportunity?.contact ? [opportunity.contact] : [];
    }
    return [];
  };

  const applyTemplate = (template: TaskTemplate | null) => {
    setSelectedTemplate(template);
    if (!template) return;

    setFormData(prev => {
      const seeded = applyPrefill(prev, {
        name: template.name,
        description: template.description,
        instructions: template.instructions,
        estimated_hours: template.estimated_hours ?? undefined,
      });
      return {
        ...seeded,
        // Provenance, not user input: it always names the template currently
        // selected, even if the user switches to a second one.
        created_from_template: template.id,
        // A select still holding its initial default is not a choice the user
        // made, so the template's own categorisation wins until they change it.
        task_type: prev.task_type === 'other' ? template.task_type : prev.task_type,
        priority: prev.priority === 'medium' ? template.priority : prev.priority,
      };
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.name) {
      const payload = { ...formData, related_contacts: inheritedContactIds() };
      // Clean up the data before sending - remove undefined values
      const cleanedData = Object.entries(payload).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== '') {
          (acc as any)[key] = value;
        }
        return acc;
      }, {} as Partial<TaskCreateData>);

      createTask.mutate(cleanedData as TaskCreateData, {
        onSuccess: (task) => {
          onSuccess(task);
          onClose();
          // Reset form
          setFormData({
            name: '',
            description: '',
            task_type: 'other',
            priority: 'medium',
            status: 'not_started',
            is_all_day: false,
            completion_percentage: 0,
            related_job: prefilledJobId,
            related_opportunity: prefilledOpportunityId,
            assigned_to: undefined,
          });
          setSelectedTemplate(null);
        },
        onError: (error: any) => {
          console.error('Task creation failed:', error);
        }
      });
    }
  };

  // Helper function to format error messages
  const getErrorMessage = (error: any): string => {
    if (error?.responseData) {
      // Handle Django validation errors
      const errors = error.responseData;
      const messages: string[] = [];
      
      Object.entries(errors).forEach(([field, value]) => {
        if (Array.isArray(value)) {
          messages.push(`${field}: ${value.join(', ')}`);
        } else if (typeof value === 'string') {
          messages.push(`${field}: ${value}`);
        } else {
          messages.push(`${field}: ${JSON.stringify(value)}`);
        }
      });
      
      return messages.join('\n');
    }
    
    return error?.message || 'An error occurred while creating the task';
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: '90vw', maxWidth: 800, overflow: 'auto' }}>
        <ModalClose />
        <Typography level="h4">Create New Task</Typography>

        {createTask.isError && (
          <Alert color="danger" sx={{ mt: 2 }}>
            {getErrorMessage(createTask.error)}
          </Alert>
        )}

        {(jobContext.isError || opportunityContext.isError) && <Alert color="danger">Unable to load related contact details. <Button onClick={() => { jobContext.refetch(); opportunityContext.refetch(); }}>Retry</Button></Alert>}
        <form onSubmit={handleSubmit}>
          <TaskRecordPicker label="Start from template" endpoint="/api/task-templates/?is_archived=false" value={selectedTemplate?.id} onChange={setSelectedTemplate} />
          {selectedTemplate && <Box sx={{ p: 2, bgcolor: 'background.level1', borderRadius: 'sm', mt: 1 }}>
            <Typography level="title-sm">Preview: {selectedTemplate.name}</Typography>
            <Typography level="body-sm">{selectedTemplate.description}</Typography>
            <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>{selectedTemplate.instructions}</Typography>
            <Button size="sm" onClick={() => applyTemplate(selectedTemplate)}>Fill blank fields from template</Button>
          </Box>}

          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Left Column */}
            <Grid xs={12} md={6}>
              <Stack spacing={2}>
                <FormControl required>
                  <FormLabel>Task Name</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter task name"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Enter task description"
                    minRows={3}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Instructions</FormLabel>
                  <Textarea
                    value={formData.instructions}
                    onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                    placeholder="Enter detailed instructions for completing this task"
                    minRows={3}
                  />
                </FormControl>
              </Stack>
            </Grid>

            {/* Right Column */}
            <Grid xs={12} md={6}>
              <Stack spacing={2}>
                <Grid container spacing={2}>
                  <Grid xs={6}>
                    <FormControl>
                      <FormLabel>Task Type</FormLabel>
                      <Select
                        value={formData.task_type}
                        onChange={(_, value) => setFormData({ ...formData, task_type: value as TaskType })}
                      >
                        {(taskTypeOptions || []).map((option) => (
                          <Option key={option.value} value={option.value}>
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid xs={6}>
                    <FormControl>
                      <FormLabel>Priority</FormLabel>
                      <Select
                        value={formData.priority}
                        onChange={(_, value) => setFormData({ ...formData, priority: value as TaskPriority })}
                      >
                        <Option value="low">Low</Option>
                        <Option value="medium">Medium</Option>
                        <Option value="high">High</Option>
                        <Option value="critical">Critical</Option>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <TaskRecordPicker label="Related Job" endpoint="/api/jobs/" value={formData.related_job} onChange={record => setFormData({ ...formData, related_job: record?.id ?? undefined, related_opportunity: record ? undefined : formData.related_opportunity })} />

                <TaskRecordPicker label="Related Opportunity" endpoint="/api/crm/opportunities/" value={formData.related_opportunity} onChange={record => setFormData({ ...formData, related_opportunity: record?.id ?? undefined, related_job: record ? undefined : formData.related_job })} />

                <FormControl>
                  <FormLabel>Assigned To</FormLabel>
                  <Select
                    value={formData.assigned_to?.toString() || ''}
                    onChange={(_, value) => setFormData({ ...formData, assigned_to: value ? parseInt(value) : undefined })}
                    placeholder="Select a user"
                    disabled={usersLoading}
                    endDecorator={usersLoading ? <CircularProgress size="sm" /> : null}
                  >
                    <Option value="">None</Option>
                    {(users || []).map((user) => (
                      <Option key={user.id} value={user.id.toString()}>
                        {user.first_name} {user.last_name}
                      </Option>
                    ))}
                  </Select>
                </FormControl>

                <Grid container spacing={2}>
                  <Grid xs={12} sm={6}>
                    <TaskScheduleFields timezone={timezone} due={formData.due_date} allDay={formData.is_all_day} assignedTo={formData.assigned_to} onChange={(due, allDay) => setFormData({ ...formData, due_date: due ?? undefined, is_all_day: allDay })} />
                  </Grid>
                  <Grid xs={12} sm={6}>
                    <FormControl>
                      <FormLabel>Est. Hours</FormLabel>
                      <Input
                        type="number"
                        value={formData.estimated_hours || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            estimated_hours: e.target.value ? parseFloat(e.target.value) : undefined
                          })
                        }
                        placeholder="0"
                        slotProps={{
                          input: {
                            min: 0,
                            step: 0.5,
                          },
                        }}
                      />
                    </FormControl>
                  </Grid>
                </Grid>

                
              </Stack>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button variant="outlined" color="neutral" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={createTask.isPending}
              disabled={!formData.name?.trim() || (!!formData.related_job && !jobContext.isSuccess) || (!!formData.related_opportunity && !opportunityContext.isSuccess)}
            >
              Create Task
            </Button>
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
};